import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconPencil, IconPlayerPause, IconPlayerPlay, IconTrash } from '@tabler/icons-react'
import { capitalize, isEmpty } from 'lodash'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { PageHeader } from '@/components/headers/PageHeader'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { BaltoLogo } from '@/components/BaltoLogo'
import { Pill } from '@/components/pills/Pill'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { ModalActions } from '@/components/modals/ModalActions'
import { openModal } from '@/reducers/ui/ui.redux'
import { formatTime } from '@/utils/helpers'
import {
  fetchCampaigns,
  updateCampaign,
  deleteCampaign,
} from '@/reducers/outbound/outboundCampaigns.actions'

import { CampaignWizard } from './components/forms/Campaigns/CreateCampaignWizard'
import { ScheduleCallModal } from './components/forms/ScheduledCalls/ScheduleCallModal'
import { ScheduledCallsTab } from './ScheduledCallsTab'

import './ContactListsPage.scss'

const TAB = { CAMPAIGNS: 'campaigns', SCHEDULED_CALLS: 'scheduledCalls' }

const STATUS_PILL_PROPS = {
  active: { success: true },
  paused: { warning: true },
  draft: { informative: true },
}

const buildColumns = (onSelectCampaign) => [
  {
    accessor: 'name',
    label: 'Name',
    isSearchable: true,
    format: (value, row) => (
      <Button link onClick={() => onSelectCampaign(row.id)}>
        {value}
      </Button>
    ),
  },
  {
    accessor: 'status',
    label: 'Status',
    isSearchable: true,
    format: (value) => (
      <Pill small {...(STATUS_PILL_PROPS[value] || {})}>
        {capitalize(value)}
      </Pill>
    ),
  },
  {
    accessor: 'contact_lists',
    label: 'Contact Lists',
    format: (value) => {
      if (isEmpty(value)) {
        return ''
      }
      if (value.length === 1) {
        return value[0].name
      }
      return (
        <InteractiveTooltip
          content={value.map((list) => (
            <div key={list.id}>{list.name}</div>
          ))}
        >
          <span>{value[0].name}...</span>
        </InteractiveTooltip>
      )
    },
  },
  {
    accessor: 'created_at',
    label: 'Created At',
    format: (value) => formatTime(value),
  },
]

export const CampaignsPage = () => {
  const dispatch = useDispatch()
  const { currentlyOpenModalId } = useSelector((state) => state.ui)
  const { data, loading } = useSelector((state) => state.outboundCampaigns)
  const { campaigns } = data
  const [editingCampaign, setEditingCampaign] = useState(null)
  const [activeTab, setActiveTab] = useState(TAB.CAMPAIGNS)

  useEffect(() => {
    dispatch(fetchCampaigns())
  }, [])

  // A campaign's analytics live on the Togo Analytics page's Outbound tab; open
  // it in a new window so the campaigns list stays put behind it.
  const handleOpenCampaignAnalytics = (campaignId) => {
    window.open(`/togo/analytics?tab=outbound&campaign=${campaignId}`, '_blank', 'noopener')
  }

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign)
    dispatch(openModal('editCampaign'))
  }

  const handleToggleStatus = (campaign) => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active'
    dispatch(updateCampaign(campaign.id, { status: newStatus }))
  }

  const handleDelete = (campaignId) => {
    dispatch(deleteCampaign(campaignId))
  }

  const actions = [
    {
      label: 'Edit',
      icon: <IconPencil />,
      fn: (event, row) => {
        handleEdit(row)
      },
    },
    {
      label: 'Toggle Status',
      icon: <IconPlayerPlay />,
      trigger: (row) => {
        const isPaused = row.status === 'active'
        return (
          <Button
            basic
            compact
            square
            icon={isPaused ? <IconPlayerPause /> : <IconPlayerPlay />}
            onClick={() => handleToggleStatus(row)}
            data-testid="toggle-status-button"
          />
        )
      },
    },
    {
      label: 'Delete',
      trigger: (row) => {
        return (
          <ButtonAndFormModal
            popup
            icon={<IconTrash />}
            buttonProps={{ basic: true, small: true, square: true }}
            modalId={`campaigns/delete/${row.id}`}
            modalTitle={`Delete "${row.name}"`}
            modalProps={{ size: 'tiny' }}
            modalContent={
              <p>Are you sure you want to delete this campaign? This action cannot be undone.</p>
            }
            form={<ModalActions onSubmit={() => handleDelete(row.id)} isDelete />}
          />
        )
      },
    },
  ]

  const isCampaignsTab = activeTab === TAB.CAMPAIGNS

  return (
    <>
      <PageHeader
        title="Campaigns"
        actions={
          isCampaignsTab ? (
            <Button brand onClick={() => dispatch(openModal('createCampaign'))}>
              Create Campaign
            </Button>
          ) : (
            <Button brand onClick={() => dispatch(openModal('scheduleCall'))}>
              Schedule Call
            </Button>
          )
        }
      />
      <SegmentedControl
        className="outbound-tabs"
        items={[
          {
            accessor: TAB.CAMPAIGNS,
            active: isCampaignsTab,
            onClick: () => setActiveTab(TAB.CAMPAIGNS),
            label: 'Campaigns',
            dataTestId: 'campaigns-tab',
          },
          {
            accessor: TAB.SCHEDULED_CALLS,
            active: !isCampaignsTab,
            onClick: () => setActiveTab(TAB.SCHEDULED_CALLS),
            label: 'Scheduled Calls',
            dataTestId: 'scheduled-calls-tab',
          },
        ]}
      />
      {currentlyOpenModalId === 'createCampaign' && <CampaignWizard />}
      {currentlyOpenModalId === 'editCampaign' && <CampaignWizard campaign={editingCampaign} />}
      {currentlyOpenModalId === 'scheduleCall' && <ScheduleCallModal />}
      {isCampaignsTab &&
        (!loading.campaigns && isEmpty(campaigns) ? (
          <div className="outbound-empty-state">
            <div className="outbound-empty-state__logo">
              <BaltoLogo width="200" height="150" fill="var(--color-neutral)" />
            </div>
            <div className="outbound-empty-state__title">Create your first outbound campaign</div>
            <p className="outbound-empty-state__description">
              Run automated outbound calls with AI agents to remind customers, recover lapsed
              policies, schedule appointments, and more.
            </p>
          </div>
        ) : (
          <Card className="no-padding">
            <AdvancedTable
              rows={campaigns}
              columns={buildColumns(handleOpenCampaignAnalytics)}
              actions={actions}
              defaultOrderBy="name"
              index="id"
              loading={loading.campaigns}
              pagination
              stickyAction
            />
          </Card>
        ))}
      {!isCampaignsTab && <ScheduledCallsTab />}
    </>
  )
}
